import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Camera, Save } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, incomeBracketsFor } from "@/lib/constants";
import VideoUploader from "@/components/VideoUploader";

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        dob: user.dob || "",
        country: user.country || "India",
        location: user.location || "",
        height_cm: user.height_cm || 170,
        marital_status: user.marital_status || "Never Married",
        religion: user.religion || "",
        community: user.community || "",
        mother_tongue: user.mother_tongue || "",
        education: user.education || "",
        profession: user.profession || "",
        income: user.income || "",
        diet: user.diet || "",
        drinking: user.drinking || "",
        smoking: user.smoking || "",
        about_me: user.about_me || "",
        phone: user.phone || "",
        partner_preferences: user.partner_preferences || { age_min: 22, age_max: 32, religion: "", location: "" },
      });
    }
  }, [user]);

  const update = (patch) => setForm({ ...form, ...patch });
  const updatePref = (patch) => setForm({ ...form, partner_preferences: { ...form.partner_preferences, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/me", { ...form, height_cm: Number(form.height_cm) });
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="my-profile-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="overline">My profile</p>
        <h1 className="font-serif text-4xl text-brand-ink mt-2">Edit your details</h1>

        <div className="mt-8 grid md:grid-cols-12 gap-8">
          <aside className="md:col-span-4">
            <div className="card-elegant p-5 text-center">
              <div className="relative w-40 h-40 mx-auto rounded-full overflow-hidden bg-brand-muted border-2 border-brand-gold/30">
                {user.photo_url ? <img src={fileUrl(user.photo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-brand-inkMute text-xs">No photo</div>}
              </div>
              <label className="btn-ghost inline-flex items-center gap-2 mt-4 cursor-pointer text-sm" data-testid="profile-upload-label">
                <Camera className="h-4 w-4" /> Change photo
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-upload-input" />
              </label>
              <p className="text-xs text-brand-inkLight mt-4">{user.profile_completion}% complete</p>
              <div className="h-1.5 bg-brand-muted rounded-full overflow-hidden mt-2">
                <div className="h-full bg-gradient-to-r from-brand-maroon to-brand-gold" style={{ width: `${user.profile_completion}%` }} />
              </div>
            </div>
          </aside>

          <main className="md:col-span-8 space-y-6">
            <Section title="Basics">
              <Row><Field label="Full name"><input value={form.name || ""} onChange={(e) => update({ name: e.target.value })} className="input-elegant" data-testid="profile-name" /></Field>
                <Field label="Date of birth"><input type="date" value={form.dob || ""} onChange={(e) => update({ dob: e.target.value })} className="input-elegant" /></Field></Row>
              <Row><Field label="Location"><input value={form.location || ""} onChange={(e) => update({ location: e.target.value })} className="input-elegant" /></Field>
                <Field label="Country">
                  <select value={form.country || "India"} onChange={(e) => update({ country: e.target.value })} className="input-elegant" data-testid="profile-country">
                    {COUNTRIES.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
                  </select>
                </Field></Row>
              <Row><Field label="Height (cm)"><input type="number" value={form.height_cm || ""} onChange={(e) => update({ height_cm: e.target.value })} className="input-elegant" /></Field>
              <Field label="Marital status">
                <select value={form.marital_status || ""} onChange={(e) => update({ marital_status: e.target.value })} className="input-elegant">
                  <option>Never Married</option><option>Divorced</option><option>Widowed</option><option>Awaiting Divorce</option>
                </select>
              </Field></Row>
              <Row><Field label="Phone"><input value={form.phone || ""} onChange={(e) => update({ phone: e.target.value })} className="input-elegant" /></Field><div /></Row>
            </Section>

            <Section title="Background">
              <Row><Field label="Religion">
                <select value={form.religion || ""} onChange={(e) => update({ religion: e.target.value })} className="input-elegant">
                  <option value="">Select</option><option>Hindu</option><option>Muslim</option><option>Christian</option><option>Sikh</option><option>Jain</option><option>Buddhist</option><option>Parsi</option><option>Other</option>
                </select>
              </Field>
              <Field label="Community"><input value={form.community || ""} onChange={(e) => update({ community: e.target.value })} className="input-elegant" /></Field></Row>
              <Row><Field label="Mother tongue"><input value={form.mother_tongue || ""} onChange={(e) => update({ mother_tongue: e.target.value })} className="input-elegant" /></Field>
              <Field label="Education"><input value={form.education || ""} onChange={(e) => update({ education: e.target.value })} className="input-elegant" /></Field></Row>
              <Row><Field label="Profession"><input value={form.profession || ""} onChange={(e) => update({ profession: e.target.value })} className="input-elegant" /></Field>
              <Field label="Income"><select value={form.income || ""} onChange={(e) => update({ income: e.target.value })} className="input-elegant"><option value="">Select</option>{incomeBracketsFor(form.country).map((b) => <option key={b} value={b}>{b}</option>)}</select></Field></Row>
            </Section>

            <Section title="Lifestyle & About">
              <Row>
                <Field label="Diet"><select value={form.diet || ""} onChange={(e) => update({ diet: e.target.value })} className="input-elegant"><option value="">Select</option><option>Vegetarian</option><option>Non-Vegetarian</option><option>Eggetarian</option><option>Vegan</option></select></Field>
                <Field label="Drinking"><select value={form.drinking || ""} onChange={(e) => update({ drinking: e.target.value })} className="input-elegant"><option value="">Select</option><option>Never</option><option>Socially</option><option>Often</option></select></Field>
              </Row>
              <Row>
                <Field label="Smoking"><select value={form.smoking || ""} onChange={(e) => update({ smoking: e.target.value })} className="input-elegant"><option value="">Select</option><option>Never</option><option>Socially</option><option>Often</option></select></Field>
                <div />
              </Row>
              <Field label="About me"><textarea rows={5} value={form.about_me || ""} onChange={(e) => update({ about_me: e.target.value })} className="input-elegant" /></Field>
            </Section>

            <VideoUploader user={user} onChange={refresh} />

            <Section title="Partner preferences">
              <Row>
                <Field label="Age min"><input type="number" value={form.partner_preferences?.age_min || 22} onChange={(e) => updatePref({ age_min: Number(e.target.value) })} className="input-elegant" /></Field>
                <Field label="Age max"><input type="number" value={form.partner_preferences?.age_max || 32} onChange={(e) => updatePref({ age_max: Number(e.target.value) })} className="input-elegant" /></Field>
              </Row>
              <Row>
                <Field label="Preferred religion"><input value={form.partner_preferences?.religion || ""} onChange={(e) => updatePref({ religion: e.target.value })} className="input-elegant" /></Field>
                <Field label="Preferred location"><input value={form.partner_preferences?.location || ""} onChange={(e) => updatePref({ location: e.target.value })} className="input-elegant" /></Field>
              </Row>
            </Section>

            <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="profile-save"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}</button>
          </main>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-elegant p-6">
      <h2 className="font-serif text-2xl text-brand-ink mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children }) { return <div className="grid grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return <div><label className="overline">{label}</label><div className="mt-1">{children}</div></div>;
}
